/**
 * use-chat-report.ts — Hook cho báo cáo "Hiệu quả Chat → Đơn hàng".
 *
 * GET /api/v1/reports/chat-to-order — API chỉ trả SỐ ĐẾM GỐC (revenue là VND
 * nguyên); mọi tỉ lệ (msgPerFriend, afterHoursPct, convRate, aiPct) và badge
 * trend so kỳ trước đều tính ở FE (xem chat-order-report.tsx).
 *
 * orders/revenue/aiOrders = null khi tổ chức CHƯA kết nối nguồn đơn hàng
 * (meta.orderSource.connected = false) — FE hiện "Chưa kết nối nguồn đơn hàng"
 * + link Tích hợp thay vì số 0. Nguồn đơn: external_orders (Evotech…).
 */
import { useApiQuery } from '@/hooks/use-api'

export interface ChatReportMetrics {
  friends: number
  msgIn: number
  afterHours: number
  chatters: number
  orders: number | null
  revenue: number | null
  aiOrders: number | null
}

export interface ChatReportTag {
  name: string
  color: string
  bg: string
  count: number
}

export interface ChatReportResult {
  label: string
  current: ChatReportMetrics
  previous?: ChatReportMetrics
  tags: ChatReportTag[]
  meta: {
    afterHoursWindow: string
    timezone: string
    salesNote?: string
    scopeNote?: string
    /** Nguồn đơn hàng đồng bộ (Evotech…) — chưa kết nối thì các số chốt là null. */
    orderSource?: { connected: boolean; sources: string[] }
  }
}

export type ChatReportPeriod = 'day' | 'week' | 'month' | 'year' | 'all' | 'custom'
export type ChatReportCompare = '7d' | 'month' | 'quarter' | 'year'

/** Chế độ đang chọn trên bộ lọc kỳ — đúng 3 nhánh của mock bàn giao. */
export type ChatReportMode =
  | { kind: 'period'; period: 'day' | 'week' | 'month' | 'year' | 'all' }
  | { kind: 'compare'; compare: ChatReportCompare }
  | { kind: 'custom'; from: string; to: string }

/** Phạm vi dữ liệu: nhóm kênh tương tác hoặc một tài khoản cụ thể (accountId thắng). */
export interface ChatReportScope {
  channel?: string // zalo_user | zalo_oa | facebook | ecommerce | other
  accountId?: string
}

export function modeToParams(mode: ChatReportMode, scope?: ChatReportScope): Record<string, string> {
  const base: Record<string, string> =
    mode.kind === 'compare'
      ? { compare: mode.compare }
      : mode.kind === 'custom'
        ? { period: 'custom', from: mode.from, to: mode.to }
        : { period: mode.period }
  if (scope?.accountId) base.accountId = scope.accountId
  else if (scope?.channel) base.channel = scope.channel
  return base
}

export function useChatReport(mode: ChatReportMode, scope?: ChatReportScope) {
  const params = modeToParams(mode, scope)
  return useApiQuery<ChatReportResult>(['chat-report', params], '/reports/chat-to-order', params)
}

/**
 * Sáu thẻ KPI đầu màn Tổng quan.
 *
 * Dùng CHUNG `mode` + `scope` với báo cáo Chat → Đơn, nên một bộ lọc đặt trên
 * cùng trang là cả trang đổi theo. Backend giải kỳ bằng cùng một hàm, không có
 * chuyện thẻ hiểu "tháng" một kiểu còn báo cáo hiểu một kiểu.
 */
export interface DashboardKpi {
  periodLabel: string
  from: string
  to: string
  /** Theo kỳ đang lọc. */
  messagesInRange: number
  newContactsInRange: number
  appointmentsInRange: number
  /** Ảnh chụp HIỆN TẠI — không đổi theo kỳ lọc. */
  unrepliedConversations: number
  unreadConversations: number
  unreadMessages: number
  totalContacts: number
}

export function useDashboardKpi(mode: ChatReportMode, scope?: ChatReportScope) {
  const params = modeToParams(mode, scope)
  return useApiQuery<DashboardKpi>(['dashboard-kpi', params], '/dashboard/kpi', params)
}
